import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  RefreshCw,
  MoreHorizontal,
  ChevronDown,
  Clock,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Card, CardContent } from '../../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../components/ui/custom-dropdown-menu';
import { DeleteConfirmationDialog } from '../../../../components/ui/DeleteConfirmationDialog';
import { Notification as customNotification } from '../../../../components/ui/custom-notification';
import { useTeamMembersReduxHook } from '../../../project/hooks/useTeamMembersRedux';
import {
  createProjectEmailConfig,
  deleteProjectEmailConfig,
  listProjectEmailConfigs,
  updateProjectEmailConfig,
  type ProjectEmailConfig,
} from '../../../../services/projectEmailConfigApi';
import {
  formatTimeWithAmPm,
  getBrowserTimezoneLabel,
  getDailyUploadSummarySchedule,
  updateDailyUploadSummarySchedule,
  utcTimeToLocalTimeAmPm,
} from '../../../../services/dailyUploadSummaryScheduleApi';
import { cn } from '../../../../lib/utils';

/** Feature keys stored in features JSON. Add more keys later as needed. */
const EMAIL_ACCESS_FEATURES = [{ key: 'pipefitter', label: 'Pipefitter' }] as const;

type FeatureKey = (typeof EMAIL_ACCESS_FEATURES)[number]['key'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyFeatures = (): Record<FeatureKey, boolean> => ({ pipefitter: false });

interface UserOption {
  id: string;
  name: string;
  searchText: string;
}

/** In-dialog user picker — avoids portaled Select search focus issues inside Modal. */
const UserCombobox: React.FC<{
  value: string;
  onChange: (displayName: string) => void;
  options: UserOption[];
  loading?: boolean;
  disabled?: boolean;
}> = ({ value, onChange, options, loading = false, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.searchText.toLowerCase().includes(q) || o.name.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-[#E2E5EA] bg-white px-3 py-2 text-[13px] text-left',
          'hover:border-heritage-grey focus:outline-none focus:ring-2 focus:ring-hero-red/30 focus:border-hero-red',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-hero-red ring-2 ring-hero-red/30'
        )}
      >
        <span className={cn('truncate', value ? 'text-text-grey' : 'text-heritage-grey')}>
          {loading ? 'Loading users...' : value || 'Select user...'}
        </span>
        <ChevronDown className={cn('h-4 w-4 text-heritage-grey shrink-0 ml-2 transition-transform', open && 'rotate-180')} />
      </button>

      {open && !loading && (
        <div className="absolute left-0 right-0 z-[70] mt-1 rounded-md border border-[#E2E5EA] bg-white shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[#E2E5EA]">
            <Input
              ref={searchRef}
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="h-8 text-[13px] placeholder:text-heritage-grey"
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setOpen(false);
                }
                if (e.key === 'Enter' && filtered.length > 0) {
                  e.preventDefault();
                  onChange(filtered[0].name);
                  setOpen(false);
                }
              }}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-[13px] text-heritage-grey">
                {search.trim() ? 'No results found' : 'No options available'}
              </p>
            ) : (
              filtered.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={cn(
                    'w-full px-3 py-2 text-left text-[13px] hover:bg-page-bg',
                    value === option.name ? 'text-blueprint-blue font-medium bg-page-bg' : 'text-text-grey'
                  )}
                  onClick={() => {
                    onChange(option.name);
                    setOpen(false);
                  }}
                >
                  <span className="block truncate">{option.name}</span>
                  <span className="block truncate text-[11px] text-heritage-grey">{option.id}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AdminEmailConfigurationProps {
  canEdit?: boolean;
}

export const AdminEmailConfiguration: React.FC<AdminEmailConfigurationProps> = ({
  canEdit = true,
}) => {
  const [rows, setRows] = useState<ProjectEmailConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ProjectEmailConfig | null>(null);
  const [emailMode, setEmailMode] = useState<'user' | 'custom'>('user');
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState<Record<FeatureKey, boolean>>(emptyFeatures());
  const [formError, setFormError] = useState('');

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<ProjectEmailConfig | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  /** HH:mm in UTC — matches API storage and the time picker value. */
  const [utcScheduleTime, setUtcScheduleTime] = useState('16:30');
  const [savedUtcTime, setSavedUtcTime] = useState<string | null>(null);
  const [scheduleError, setScheduleError] = useState('');

  const timezoneLabel = useMemo(() => getBrowserTimezoneLabel(), []);

  const { users, usersLoading, fetchUsers } = useTeamMembersReduxHook();

  const userEmailOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ id: string; name: string; searchText: string }> = [];
    (users || []).forEach((user: any) => {
      const email = String(user?.email || '').trim().toLowerCase();
      if (!email || seen.has(email)) return;
      seen.add(email);
      const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
      const label = fullName || email;
      options.push({
        id: email,
        name: label,
        searchText: `${fullName} ${email}`.trim(),
      });
    });
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProjectEmailConfigs();
      setRows(data);
    } catch (error: any) {
      customNotification.error(
        'Failed to load email configuration',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (isModalOpen) fetchUsers();
  }, [isModalOpen, fetchUsers]);

  const resetForm = () => {
    setEditingRow(null);
    setEmailMode('user');
    setSelectedUserEmail('');
    setCustomEmail('');
    setSelectedFeatures(emptyFeatures());
    setFormError('');
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (row: ProjectEmailConfig) => {
    setEditingRow(row);
    setEmailMode('custom');
    setSelectedUserEmail('');
    setCustomEmail(row.email);
    const next = emptyFeatures();
    EMAIL_ACCESS_FEATURES.forEach((feature) => {
      next[feature.key] = Boolean(row.features?.[feature.key]);
    });
    setSelectedFeatures(next);
    setFormError('');
    setIsModalOpen(true);
  };

  const resolveEmail = (): string => {
    if (editingRow) return editingRow.email;
    if (emailMode === 'user') {
      return userEmailOptions.find((o) => o.name === selectedUserEmail)?.id || '';
    }
    return customEmail.trim().toLowerCase();
  };

  const buildFeaturesPayload = (map: Record<FeatureKey, boolean>) => {
    const features: Record<string, boolean> = {};
    EMAIL_ACCESS_FEATURES.forEach((feature) => {
      features[feature.key] = Boolean(map[feature.key]);
    });
    return features;
  };

  const handleToggleFeature = async (
    row: ProjectEmailConfig,
    featureKey: FeatureKey,
    enabled: boolean
  ) => {
    if (!canEdit) return;
    const toggleId = `${row.email_config_id}:${featureKey}`;
    setTogglingKey(toggleId);
    try {
      const features = {
        ...(row.features || {}),
        [featureKey]: enabled,
      };
      await updateProjectEmailConfig(row.email_config_id, { features });
      await loadRows();
    } catch (error: any) {
      customNotification.error(
        'Update failed',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setTogglingKey(null);
    }
  };

  const handleSave = async () => {
    const email = resolveEmail();
    const features = buildFeaturesPayload(selectedFeatures);
    const hasAny = Object.values(features).some(Boolean);

    if (!email || !EMAIL_REGEX.test(email)) {
      setFormError('Enter a valid email address.');
      return;
    }
    if (!hasAny) {
      setFormError('Select at least one report email access option.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      if (editingRow) {
        await updateProjectEmailConfig(editingRow.email_config_id, { features });
        customNotification.success('Email configuration updated');
      } else {
        await createProjectEmailConfig({ email, features });
        customNotification.success('Email configuration added');
      }
      setIsModalOpen(false);
      resetForm();
      await loadRows();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        error?.message ||
        'Save failed';
      setFormError(msg);
      customNotification.error('Save failed', msg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    setDeleting(true);
    try {
      await deleteProjectEmailConfig(rowToDelete.email_config_id);
      customNotification.success('Email configuration deleted');
      setIsDeleteOpen(false);
      setRowToDelete(null);
      await loadRows();
    } catch (error: any) {
      customNotification.error(
        'Delete failed',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const openScheduleModal = async () => {
    setIsScheduleOpen(true);
    setScheduleError('');
    setScheduleLoading(true);
    try {
      const schedule = await getDailyUploadSummarySchedule();
      setSavedUtcTime(schedule.utcTime);
      setUtcScheduleTime(schedule.utcTime);
    } catch (error: any) {
      setSavedUtcTime(null);
      setUtcScheduleTime('16:30');
      customNotification.error(
        'Failed to load schedule',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!canEdit) return;
    const utcTime = String(utcScheduleTime || '').trim();
    if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(utcTime)) {
      setScheduleError('Enter a valid UTC time (HH:mm).');
      return;
    }

    setScheduleSaving(true);
    setScheduleError('');
    try {
      const schedule = await updateDailyUploadSummarySchedule(utcTime);
      setSavedUtcTime(schedule.utcTime);
      setUtcScheduleTime(schedule.utcTime);
      customNotification.success('Daily upload summary schedule updated');
      setIsScheduleOpen(false);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        error?.message ||
        'Save failed';
      setScheduleError(msg);
      customNotification.error('Schedule update failed', msg);
    } finally {
      setScheduleSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#EB1C37]" />
            Email Configuration
          </h2>

        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadRows}
            disabled={loading}
            className="gap-1.5"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openScheduleModal}
            disabled={loading || scheduleLoading}
            className="gap-1.5"
          >
            {scheduleLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Clock className="h-4 w-4" />
            )}
            Schedule Email
          </Button>

          {canEdit && (

            <Button type="button" size="sm" onClick={openAddModal} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add Email
            </Button>
          )}
        </div>
      </div>

      <Card className="border border-[#E2E5EA] bg-white shadow-none">
        <CardContent className="p-0">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-heritage-grey gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading email configuration...
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <Mail className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-900">No email configurations yet</p>
              <p className="text-sm text-heritage-grey mt-1">
                Add a recipient and enable feature email access.
              </p>
              {canEdit && (
                <Button type="button" size="sm" className="mt-4 gap-1.5" onClick={openAddModal}>
                  <Plus className="h-4 w-4" />
                  Add Email
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-page-bg/60">
                    <TableHead className="min-w-[240px] text-[12px] font-semibold text-text-grey">
                      Email
                    </TableHead>
                    {EMAIL_ACCESS_FEATURES.map((feature) => (
                      <TableHead
                        key={feature.key}
                        className="text-center text-[12px] font-semibold text-text-grey min-w-[120px]"
                      >
                        {feature.label}
                      </TableHead>
                    ))}
                    <TableHead className="w-[88px] text-right text-[12px] font-semibold text-text-grey">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.email_config_id} className="hover:bg-page-bg/40">
                      <TableCell className="text-[13px] text-gray-900 break-all">{row.email}</TableCell>
                      {EMAIL_ACCESS_FEATURES.map((feature) => {
                        const checked = Boolean(row.features?.[feature.key]);
                        const busy = togglingKey === `${row.email_config_id}:${feature.key}`;
                        return (
                          <TableCell key={feature.key} className="text-center">
                            <div className="flex items-center justify-center">
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin text-heritage-grey" />
                              ) : (
                                <Checkbox
                                  checked={checked}
                                  disabled={!canEdit}
                                  onCheckedChange={(value: boolean | 'indeterminate') =>
                                    handleToggleFeature(row, feature.key, value === true)
                                  }
                                  aria-label={`${feature.label} for ${row.email}`}
                                />
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right">
                        {canEdit ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors ml-auto">
                              <MoreHorizontal className="h-4 w-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditModal(row)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-readable-red"
                                onClick={() => {
                                  setRowToDelete(row);
                                  setIsDeleteOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-xs text-heritage-grey">View only</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isModalOpen}
        onOpenChange={(open: boolean) => {
          setIsModalOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-lg overflow-visible">
          <DialogHeader>
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue">
              {editingRow ? 'Edit Email' : 'Add Email'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2 overflow-visible">
            {!editingRow && (
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-text-grey">Email Source</Label>
                <div className="inline-flex bg-page-bg border border-[#E2E5EA] rounded-lg p-[3px]">
                  <button
                    type="button"
                    onClick={() => setEmailMode('user')}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 ${emailMode === 'user'
                      ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                      : 'text-heritage-grey hover:text-blueprint-blue'
                      } disabled:opacity-50 disabled:pointer-events-none`}
                  >
                    User
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmailMode('custom')}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-all duration-150 ${emailMode === 'custom'
                      ? 'bg-white text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                      : 'text-heritage-grey hover:text-blueprint-blue'
                      } disabled:opacity-50 disabled:pointer-events-none`}
                  >
                    Custom Email
                  </button>
                </div>
              </div>
            )}

            {editingRow || emailMode === 'custom' ? (
              <div className="space-y-1.5">
                <Label htmlFor="email-config-email" className="text-[12px] font-medium text-text-grey">
                  Email <span className="text-hero-red">*</span>
                </Label>
                <Input
                  id="email-config-email"
                  type="email"
                  placeholder="name@example.com"
                  value={customEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomEmail(e.target.value)}
                  disabled={saving || Boolean(editingRow)}
                  className="bg-white"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-[12px] font-medium text-text-grey">
                  User <span className="text-hero-red">*</span>
                </Label>
                <div className="relative mt-1">
                  <UserCombobox
                    value={selectedUserEmail}
                    onChange={setSelectedUserEmail}
                    options={userEmailOptions}
                    loading={usersLoading}
                    disabled={saving || usersLoading}
                  />
                </div>
                {selectedUserEmail ? (
                  <p className="text-[11px] text-heritage-grey mt-1">
                    Email: {userEmailOptions.find((o) => o.name === selectedUserEmail)?.id || ''}
                  </p>
                ) : null}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">
                Report Email Access <span className="text-hero-red">*</span>
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-[#E2E5EA] bg-page-bg/40 p-4">
                {EMAIL_ACCESS_FEATURES.map((feature) => (
                  <label
                    key={feature.key}
                    className="flex items-center gap-2.5 cursor-pointer select-none"
                  >
                    <Checkbox
                      checked={Boolean(selectedFeatures[feature.key])}
                      disabled={saving}
                      onCheckedChange={(value: boolean | 'indeterminate') =>
                        setSelectedFeatures((prev) => ({
                          ...prev,
                          [feature.key]: value === true,
                        }))
                      }
                    />
                    <span className="text-[13px] text-gray-900">{feature.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {formError ? <p className="text-sm text-readable-red">{formError}</p> : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingRow ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isScheduleOpen}
        onOpenChange={(open: boolean) => {
          setIsScheduleOpen(open);
          if (!open) {
            setScheduleError('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue">
              Schedule Daily Email
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {scheduleLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-heritage-grey gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading schedule...
              </div>
            ) : (
              <>



                <div className="rounded-lg border border-[#E2E5EA] bg-page-bg/40 px-3 py-3 space-y-1">
                  <p className="text-[12px] font-medium text-text-grey">Current schedule</p>
                  <p className="text-[15px] font-semibold text-gray-900">
                    {savedUtcTime ? utcTimeToLocalTimeAmPm(savedUtcTime) : '—'}
                    <span className="ml-1.5 text-[12px] font-normal text-heritage-grey">
                      ({timezoneLabel})
                    </span>
                  </p>
                  {/* {savedUtcTime ? (
                    <p className="text-[11px] text-heritage-grey">
                      Email will be sent at {savedUtcTime} UTC
                    </p>
                  ) : (
                    <p className="text-[11px] text-heritage-grey">No schedule configured yet.</p>
                  )} */}
                </div>

                {canEdit ? (
                  <div className="space-y-1.5 pt-1 border-t border-[#E2E5EA]">
                    <Label
                      htmlFor="daily-summary-utc-time"
                      className="text-[12px] font-medium text-text-grey"
                    >
                      Change send time (UTC) <span className="text-hero-red">*</span>
                    </Label>
                    <Input
                      id="daily-summary-utc-time"
                      type="time"
                      value={utcScheduleTime}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setUtcScheduleTime(e.target.value)
                      }
                      disabled={scheduleSaving}
                      className="bg-white w-full"
                    />
                    <p className="text-[11px] text-heritage-grey">
                      UTC: {formatTimeWithAmPm(utcScheduleTime)}
                      <span className="ml-1.5">
                        · Local: {utcTimeToLocalTimeAmPm(utcScheduleTime)} ({timezoneLabel})
                      </span>
                    </p>
                  </div>

                ) : null}

                {scheduleError ? (
                  <p className="text-sm text-readable-red">{scheduleError}</p>
                ) : null}
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsScheduleOpen(false)}
              disabled={scheduleSaving}
            >
              Cancel
            </Button>
            {canEdit ? (
              <Button
                type="button"
                onClick={handleSaveSchedule}
                disabled={scheduleLoading || scheduleSaving || !utcScheduleTime}
              >
                {scheduleSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Schedule
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        isLoading={deleting}
        title="Delete Email Configuration"
        itemType="Email Configuration"
        itemName={rowToDelete?.email}
        itemToDelete={rowToDelete}
      />
    </div>
  );
};

export default AdminEmailConfiguration;
